import {Directive, effect} from '@angular/core';
import {FormArray, FormGroup} from '@angular/forms';
import {AbstractFormDirective, FormControlsConfig} from './abstract-form-directive';

// ========================================================================================
// HowTo in HTML!
// ========================================================================================
// In html to make this editable
// <form [formGroup]="theForm">  // as usual
//   <ng-container formArrayName="data">  // the list name should be data. see this class
// <p-table [value]="feeRows()" [rowTrackBy]="trackByIndex"> // to track changes [rowTrackBy]="trackByIndex" and avoid refresh
//   ...
// <ng-template #body let-row let-i="rowIndex">
//   <tr [formGroupName]="i"> // This class handle a Group of [FormGroup]s; This tell current
// ========================================================================================
@Directive()
export abstract class AbstractFormArrayDirective<
  T extends {data: I[]},
  I,
> extends AbstractFormDirective<T> {
  // eslint-disable-next-line @angular-eslint/prefer-inject
  protected constructor(controlConfig: FormControlsConfig<T>) {
    super(controlConfig);

    effect(() => {
      const input = this.inputObject();
      const data: I[] = input?.data;

      if (data && Array.isArray(data)) {
        this.syncFormArray(data);
      }
    });
  }

  protected abstract createRowFormGroup(item: I): FormGroup;

  protected override isFieldValueChanged(
    key: string,
    originalValue: any,
    currentValue: any,
  ): boolean {
    // Specialized check for the 'data' array in ArrayDirectives
    if (key === 'data' && Array.isArray(originalValue) && Array.isArray(currentValue)) {
      if (originalValue.length !== currentValue.length) return true;

      return originalValue.some((item, index) => {
        const formGroupValue = currentValue[index];
        // Only compare keys that actually exist in the FormGroup to avoid
        // comparing metadata which isn't in the form
        return Object.keys(formGroupValue).some(
          (prop) => !this.deepEqual((item as any)[prop], formGroupValue[prop]),
        );
      });
    }

    return super.isFieldValueChanged(key, originalValue, currentValue);
  }

  private syncFormArray(items: I[]): void {
    const currentArray = this.dataArray;

    if (!currentArray || currentArray.length !== items.length) {
      const newControls = items.map((item) => this.createRowFormGroup(item));
      const newFormArray = this.fb.array(newControls);

      this.theForm.setControl('data', newFormArray, {emitEvent: false});
      // Force refresh of signal if necessary
      this.formValueSignal.set(this.theForm.getRawValue() as T);
      return;
    }

    items.forEach((item, index) => {
      const control = currentArray.at(index) as FormGroup;
      const calculatedGroup = this.createRowFormGroup(item);
      const calculatedValues = calculatedGroup.getRawValue();

      if (!this.deepEqual(control.getRawValue(), calculatedValues)) {
        control.patchValue(calculatedValues, {emitEvent: false});
      }
    });

    this.formValueSignal.set(this.theForm.getRawValue() as T);
  }

  get dataArray(): FormArray {
    return this.theForm.get('data') as FormArray;
  }

  protected trackByIndex(index: number): number {
    return index;
  }
}
